import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  CREATE_TOKEN_REQUEST,
  createTokenSuccess,
  createTokenFailure,
  LIST_TOKENS_REQUEST,
  listTokensSuccess,
  listTokensFailure,
  UPDATE_TOKEN_REQUEST,
  updateTokenSuccess,
  updateTokenFailure,
  REVOKE_TOKEN_REQUEST,
  revokeTokenSuccess,
  revokeTokenFailure,
  REGENERATE_TOKEN_REQUEST,
  regenerateTokenSuccess,
  regenerateTokenFailure
} from '../../actions/auth/apiTokens';
import config from '../../../config';
import { Error as ErrorAction } from '../../actions/error/error';
import { getLocalUser } from '../../../utils/localUser';

const authHeaders = () => {
  const headers = getLocalUser();
  if (!headers.Authorization) {
    throw new Error('Not authenticated');
  }
  return headers;
};

// The backend wraps successful payloads as {"data": {...}} (responseJSON).
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

// Prefer the backend's message over a generic axios error string.
// Error payloads are shaped {"status": "..."} (responseStatusJSON) or
// {"message": "..."} (rate limiting). Non-JSON bodies (e.g. HTML error
// pages from the web server) fall through to the axios message.
const errorPayload = (error) => {
  const data = error?.response?.data;
  if (data?.status && typeof data.status === 'string') return data.status;
  if (data?.message && typeof data.message === 'string') return data.message;
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data;
  return error.message;
};

// Create API Token
function* createTokenSaga(action) {
  try {
    const response = yield call(axios.post,
      `${config.API_URL}/user/api-tokens`,
      action.payload,
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );

    yield put(createTokenSuccess(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(createTokenFailure(errorPayload(error)));
  }
}

// List API Tokens
function* listTokensSaga() {
  try {
    const response = yield call(axios.get,
      `${config.API_URL}/user/api-tokens`,
      { headers: authHeaders() }
    );

    yield put(listTokensSuccess(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(listTokensFailure(errorPayload(error)));
  }
}

// Update (edit) API Token
function* updateTokenSaga(action) {
  try {
    const { tokenId, changes } = action.payload;

    const response = yield call(axios.patch,
      `${config.API_URL}/user/api-tokens/${tokenId}`,
      changes,
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );

    yield put(updateTokenSuccess(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(updateTokenFailure(errorPayload(error)));
  }
}

// Revoke API Token
function* revokeTokenSaga(action) {
  try {
    const { tokenId } = action.payload;

    const response = yield call(axios.delete,
      `${config.API_URL}/user/api-tokens/${tokenId}`,
      { headers: authHeaders() }
    );

    yield put(revokeTokenSuccess({ ...unwrap(response), token_id: tokenId }));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(revokeTokenFailure(errorPayload(error)));
  }
}

// Regenerate API Token
function* regenerateTokenSaga(action) {
  try {
    const { tokenId, newName } = action.payload;
    const requestBody = newName ? { name: newName } : {};

    const response = yield call(axios.post,
      `${config.API_URL}/user/api-tokens/${tokenId}/regenerate`,
      requestBody,
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
    );

    yield put(regenerateTokenSuccess(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(regenerateTokenFailure(errorPayload(error)));
  }
}

export function* watchCreateToken() {
  yield takeLatest(CREATE_TOKEN_REQUEST, createTokenSaga);
}

export function* watchListTokens() {
  yield takeLatest(LIST_TOKENS_REQUEST, listTokensSaga);
}

export function* watchUpdateToken() {
  yield takeLatest(UPDATE_TOKEN_REQUEST, updateTokenSaga);
}

export function* watchRevokeToken() {
  yield takeLatest(REVOKE_TOKEN_REQUEST, revokeTokenSaga);
}

export function* watchRegenerateToken() {
  yield takeLatest(REGENERATE_TOKEN_REQUEST, regenerateTokenSaga);
}
